/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import queryString from 'querystring';

import { Endpoints, Infer } from 'shared';

import { ApiClient } from '../types/api/ApiClient';
import { RecoveryPassword } from '../types/authentication/RecoveryPassword';
import { OidcStatus } from '../types/authentication/SsoStatus';
import { getApiEndpoint } from '../utils/getApiEndpoint';
import { apiClientFactory } from '../utils/http-client/apiClientFactory';
import { axiosInstanceFactory } from '../utils/http-client/axiosInstanceFactory';
import { axiosJwtTokenInterceptor } from '../utils/http-client/axiosInterceptors';

class AuthenticationApiServiceClass {
  private api: ApiClient;

  constructor() {
    this.api = apiClientFactory(axiosInstanceFactory({ baseURL: getApiEndpoint() }, [axiosJwtTokenInterceptor]));
  }

  forgot = (email: string) => {
    return this.api.post(`/authentication/forgot-password`, { email });
  };

  register = (data: Infer<typeof Endpoints.AuthPostRegister>['Body']) => {
    return this.api.post<Infer<typeof Endpoints.AuthPostRegister>['Response']['200']>(
      Endpoints.AuthPostRegister.route(),
      data,
    );
  };

  logout = () => this.api.post(Endpoints.AuthPostLogout.route(), {});

  login = (data: Infer<typeof Endpoints.AuthPostLogin>['Body']) => {
    return this.api.post<Infer<typeof Endpoints.AuthPostLogin>['Response']['200']>(
      Endpoints.AuthPostLogin.route(),
      data,
    );
  };

  refresh = () =>
    this.api.post<Infer<typeof Endpoints.PostRefresh>['Response']['200']>(Endpoints.PostRefresh.route(), {});

  verify = (token: string) => {
    return this.api.get(`/authentication/verify?${queryString.encode({ token })}`);
  };

  recoverPassword = (data: RecoveryPassword) => {
    return this.api.post('/authentication/change-password', data);
  };

  checkRecoverPasswordToken = (params: { token: string }) => {
    return this.api.get(`/authentication/change-password?${queryString.encode(params)}`);
  };

  checkSSOProviders = () => this.api.get(`/authentication/sso`);

  checkOidcStatus = () => this.api.get<OidcStatus>(`/authentication/sso/status`);

  loginOidc = (data: Infer<typeof Endpoints.PostAuthenticationOidcLogin>['Body']) =>
    this.api.post<Infer<typeof Endpoints.PostAuthenticationOidcLogin>['Response']['200']>(
      `/authentication/oidc/login`,
      data,
    );

  loginOidcCallback = () =>
    this.api.get<Infer<typeof Endpoints.GetAuthenticationOidcLoginCallback>['Response']['200']>(
      '/authentication/oidc/login/callback',
    );

  loginOptions = (params: Infer<typeof Endpoints.GetAuthenticationLoginOptions>['QueryParams']) =>
    this.api.get<Infer<typeof Endpoints.GetAuthenticationLoginOptions>['Response']['200']>(
      '/authentication/login-options',
      { params },
    );
}

export const AuthenticationApiService = new AuthenticationApiServiceClass();
